import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { Icon } from '@percent/lemonade'
import { FilterChip } from '@percent/workplace-giving/common/components/FilterChip/FilterChip'
import classes from './FilterChipList.module.scss'

type FilterChipItem = {
  id: string
  label: string
  icon?: ReactNode
}

type FilterChipListProps = {
  tags: FilterChipItem[]
  selectedIds: string[]
  onToggle: (id: string) => void
  testId?: string
  ariaLabel?: string
  scrollAmount?: number
}

export function FilterChipList({
  tags,
  selectedIds,
  onToggle,
  testId,
  ariaLabel = 'Filter by cause area',
  scrollAmount = 240
}: Readonly<FilterChipListProps>) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current

    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = scrollRef.current

    if (!el) return undefined

    updateScrollState()
    el.addEventListener('scroll', updateScrollState)

    const observer = new ResizeObserver(updateScrollState)
    observer.observe(el)

    return () => {
      el.removeEventListener('scroll', updateScrollState)
      observer.disconnect()
    }
  }, [tags, updateScrollState])

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
  }

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: scrollAmount, behavior: 'smooth' })
  }

  return (
    <div className={classes.wrapper} data-testid={testId} role="group" aria-label={ariaLabel}>
      {canScrollLeft && (
        <div className={classes.fadeLeft}>
          <button className={classes.arrow} onClick={scrollLeft} aria-label="Scroll left" type="button">
            <Icon name="arrow-left" size={5} />
          </button>
        </div>
      )}
      <div className={classes.scrollContainer} ref={scrollRef}>
        {tags.map(tag => (
          <FilterChip
            key={tag.id}
            label={tag.label}
            icon={tag.icon}
            selected={selectedIds.includes(tag.id)}
            onToggle={() => onToggle(tag.id)}
            testId={`filter-chip-${tag.id}`}
          />
        ))}
      </div>
      {canScrollRight && (
        <div className={classes.fadeRight}>
          <button className={classes.arrow} onClick={scrollRight} aria-label="Scroll right" type="button">
            <Icon name="arrow-right" size={5} />
          </button>
        </div>
      )}
    </div>
  )
}
